import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { ShoppingBag, Menu, X, User, LogOut, LayoutDashboard, ChevronDown, Gift } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "./ui/dropdown-menu";

const navLinks = [
  { to: "/products", label: "Produk Digital" },
  { to: "/services", label: "Layanan" },
  { to: "/tests", label: "Tes Psikologi" },
  { to: "/events", label: "Event" },
  { to: "/subscriptions", label: "Membership" },
  { to: "/about", label: "Tentang" },
  { to: "/contact", label: "Kontak" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header className="lumina-glass sticky top-0 z-50" data-testid="navbar">
      <div className="lumina-container flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#6B4BA3] to-[#9D84D2] flex items-center justify-center">
            <span className="text-white font-serif text-xl">H</span>
          </div>
          <span className="font-serif text-2xl text-[#1A1728]">HearInsight</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.slice(1)}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${isActive ? "text-[#6B4BA3]" : "text-[#5C5870] hover:text-[#6B4BA3]"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/checkout" className="relative p-2 hover:bg-[#E8F0FA] rounded-full transition-colors" data-testid="cart-button">
            <ShoppingBag className="w-5 h-5 text-[#1A1728]" />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 bg-[#6B4BA3] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center" data-testid="cart-count">
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 px-3 py-2 hover:bg-[#E8F0FA] rounded-full transition-colors" data-testid="user-menu-trigger">
                  <div className="w-8 h-8 rounded-full bg-[#E8F0FA] flex items-center justify-center text-[#6B4BA3] font-semibold text-sm">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </div>
                  <span className="hidden md:inline text-sm font-medium">{user.name?.split(" ")[0]}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-2xl">
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                  <User className="w-4 h-4 mr-2" /> Profil & Pesanan
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/redeem")} data-testid="menu-redeem">
                  <Gift className="w-4 h-4 mr-2" /> Redeem Kode
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Keluar
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" className="hidden md:inline-flex lumina-btn-primary text-sm py-2.5 px-5" data-testid="login-link">
              Masuk
            </Link>
          )}

          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-[#E2DFED]" data-testid="mobile-menu">
          <div className="lumina-container py-4 flex flex-col gap-2">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `py-2 text-sm font-medium ${isActive ? "text-[#6B4BA3]" : "text-[#5C5870]"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {!user && (
              <Link to="/login" onClick={() => setOpen(false)} className="lumina-btn-primary mt-2">Masuk</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
